import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n/useTranslation";

interface CountdownOverlayProps {
  /** Total countdown length in ms — drives the ring depletion. */
  durationMs: number;
  /** Tap / click / Space / Enter to skip straight into the trial. */
  onSkip?: () => void;
}

const R = 52;
const CIRC = 2 * Math.PI * R;

/**
 * Pre-trial "get ready" overlay: an amber ring that shrinks (depletes) over
 * `durationMs` with a "Prepárate" / "Get Ready" label, so a new trial's
 * stimulus never appears abruptly. Mirrors the desktop/iOS countdown.
 *
 * Honors prefers-reduced-motion (ring stays static, full). Skippable by
 * click/tap and — matching desktop — by Space/Enter anywhere. The button is
 * focused on mount so screen readers announce the label naturally (an
 * aria-live region mounted together with its content never fires).
 */
export function CountdownOverlay({ durationMs, onSkip }: CountdownOverlayProps) {
  const { t } = useTranslation();
  const [deplete, setDeplete] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Static full ring for reduced-motion users — no animation.
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (mq?.matches) return;
    // Flip on the next frame so the CSS transition actually runs.
    const id = window.requestAnimationFrame(() => setDeplete(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  // Focus the overlay: announces "Get Ready" to screen readers and makes
  // Space/Enter skip work natively while the overlay is up.
  useEffect(() => {
    btnRef.current?.focus();
  }, []);

  // Window-level Space/Enter skip (desktop parity) — covers focus being
  // elsewhere. preventDefault stops the native button activation from firing
  // a second, duplicate skip when the button itself holds focus.
  useEffect(() => {
    if (!onSkip) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        onSkip();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSkip]);

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={onSkip}
      className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-bg-1/95 z-30 cursor-pointer"
      aria-label={t("common.getReady")}
    >
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="var(--color-bg-3)"
            strokeWidth="6"
          />
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="var(--color-amber)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={deplete ? CIRC : 0}
            style={{ transition: `stroke-dashoffset ${durationMs}ms linear` }}
          />
        </svg>
      </div>
      <span className="font-mono text-sm tracking-[0.18em] uppercase text-amber">
        {t("common.getReady")}
      </span>
    </button>
  );
}
